import * as THREE from 'three'
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'

export type GizmoMode = 'translate' | 'rotate'

type LoopHandle = {
  requestRender: () => void
  markSceneChanged: () => void
  setContinuousWorldUpdates: (enabled: boolean) => void
  setInteracting: (interacting: boolean) => void
}

export function useTransformGizmo(loop: LoopHandle) {
  let gizmo: TransformControls | null = null
  let helper: THREE.Object3D | null = null
  let onChange: (() => void) | null = null

  function init(
    camera: THREE.PerspectiveCamera,
    domElement: HTMLElement,
    scene: THREE.Scene,
    orbit: OrbitControls,
  ) {
    gizmo = new TransformControls(camera, domElement)
    gizmo.setSpace('world')
    gizmo.enabled = false
    gizmo.addEventListener('dragging-changed', (event) => {
      orbit.enabled = !event.value
      loop.setInteracting(!!event.value)
    })
    gizmo.addEventListener('objectChange', () => onChange?.())
    gizmo.addEventListener('change', () => loop.markSceneChanged())
    helper = gizmo.getHelper()
    helper.visible = false
    scene.add(helper)
  }

  function attach(object: THREE.Object3D, mode: GizmoMode) {
    if (!gizmo) return
    gizmo.setMode(mode)
    gizmo.attach(object)
    gizmo.enabled = true
    if (helper) helper.visible = true
    loop.setContinuousWorldUpdates(true)
    loop.markSceneChanged()
  }

  function detach() {
    if (!gizmo) return
    gizmo.detach()
    gizmo.enabled = false
    if (helper) helper.visible = false
    loop.setContinuousWorldUpdates(false)
    loop.markSceneChanged()
  }

  function isDragging(): boolean {
    return gizmo?.dragging ?? false
  }

  function setOnChange(cb: (() => void) | null) {
    onChange = cb
  }

  function dispose() {
    helper?.removeFromParent()
    gizmo?.dispose()
    gizmo = null
    helper = null
    onChange = null
  }

  return { init, attach, detach, isDragging, setOnChange, dispose }
}
